import React, {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Delete,
  Search,
  Check,
  Key,
  Add,
  ArrowBack,
  Edit,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  ListItemDecorator,
  ListItemContent,
  Avatar,
  ListItemButton,
  ListItem as MUIListItem,
  Input,
  CircularProgress,
  Typography,
  LinearProgress,
} from "@mui/joy";
import { useAuthorization } from "context/AuthorizationContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import InfiniteLoader from "react-window-infinite-loader";
import { FixedSizeList, areEqual } from "react-window";
import { ListResponse, UserIdentity } from "types";
import { GridSortModel } from "@mui/x-data-grid";
import axios from "axios";
import { toast } from "sonner";
import AutoSizer, { Size } from "react-virtualized-auto-sizer";
import { usePrevious } from "hooks";
import { useDialogManger } from "context/DialogManagerContext";
import ChangeUserPasswordModal from "views/Admin/Users/ChangeUserPasswordModal";
import UserDetailsModal from "views/Admin/Users/DetailsModal";
import { CustomNoRowsOverlay } from "components/styled_components/DataGridOverlays";

const ListItem = memo(
  ({
    data,
    index,
    style,
  }: {
    data: {
      users: UserIdentity[];
      selectedRows: string[];
      isItemLoaded: (index: number) => boolean;
      setUserDetailsModal: React.Dispatch<
        React.SetStateAction<{
          open: boolean;
          userId: string;
        }>
      >;
      setSelectedRows: React.Dispatch<React.SetStateAction<string[]>>;
    };
    index: number;
    style: CSSProperties;
  }) => {
    const {
      users,
      selectedRows,
      isItemLoaded,
      setUserDetailsModal,
      setSelectedRows,
    } = data;
    if (!isItemLoaded(index)) {
      return (
        <MUIListItem style={style} sx={{ justifyContent: "center" }}>
          <CircularProgress size="sm" variant="plain" />
        </MUIListItem>
      );
    }
    return (
      <ListItemButton
        style={style}
        sx={{
          px: 2,
          gap: 2,
        }}
        onClick={() =>
          // isAllowed("users.write")
          //   ? navigate(users[index].id)
          setUserDetailsModal({ open: true, userId: users[index].id })
        }
      >
        <ListItemDecorator>
          <Avatar
            color={
              selectedRows.includes(users[index].id) ? "primary" : "neutral"
            }
            onClick={(e) => {
              e.stopPropagation();
              if (!selectedRows.includes(users[index].id)) {
                setSelectedRows([...selectedRows, users[index].id]);
              } else {
                setSelectedRows(
                  selectedRows.filter((r) => r !== users[index].id)
                );
              }
            }}
          >
            {selectedRows.includes(users[index].id) ? (
              <Check />
            ) : (
              <>{users[index].email[0].toUpperCase()}</>
            )}
          </Avatar>
        </ListItemDecorator>
        <ListItemContent>
          <Typography>{users[index].email}</Typography>
          <Typography level="body2" noWrap>
            {users[index].firstName} {users[index].lastName}
          </Typography>
        </ListItemContent>
      </ListItemButton>
    );
  },
  areEqual
);

const UsersMobileList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserIdentity[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [sort] = useState<GridSortModel>([
    { field: "created_at", sort: "asc" },
  ]);
  const [filter, setFilter] = useState("");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [userDetailsModal, setUserDetailsModal] = useState<{
    userId: string;
    open: boolean;
  }>({
    open: false,
    userId: "",
  });
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { showDialog } = useDialogManger();
  const { isAllowed } = useAuthorization();
  let filterTimeout = useRef<NodeJS.Timeout>(null!);
  const prevFilter = usePrevious(filter);
  const itemCount = users.length < totalItems ? users.length + 1 : users.length;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<ListResponse<UserIdentity>>(
        "/api/users",
        {
          params: {
            page: page,
            limit,
            sort: `${sort[0].field}:${sort[0].sort}`,
            search: filter,
          },
        }
      );
      setUsers(data.data);
      setPage(data.page);
      setTotalItems(data.total);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot fetch users"));
      }
    }
    setLoading(false);
  }, [page, limit, sort, filter, navigate, t]);

  const fetchMoreUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get<ListResponse<UserIdentity>>(
        "/api/users",
        {
          params: {
            page: page + 1,
            limit,
            sort: `${sort[0].field}:${sort[0].sort}`,
            search: filter,
          },
        }
      );
      setUsers([...users, ...data.data]);
      setPage(data.page);
      setTotalItems(data.total);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot fetch users"));
      }
    }
    setLoading(false);
  };

  const isItemLoaded = (index: number) =>
    !(users.length < totalItems) || index < users.length;

  const loadMoreItems = loading ? () => {} : fetchMoreUsers;

  const onDeleteUsers = async () => {
    try {
      const { status } = await axios.post(`/api/users/delete`, {
        userIds: selectedRows,
      });
      if (status === 200) {
        setSelectedRows([]);
        fetchUsers();
        toast.success(t("Users successfully deleted"));
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot delete users"));
      }
    }
    setLoading(false);
  };

  const onDeleteUser = async () => {
    try {
      const { status } = await axios.delete(`/api/users/${selectedRows[0]}`);
      if (status === 200) {
        setSelectedRows([]);
        fetchUsers();
        toast.success(t("User successfully deleted"));
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        console.log(error.response?.data);
        toast.error(error.response?.data.error);
      } else {
        console.log(error);
        toast.error(t("Something went wrong! Cannot delete user"));
      }
    }
    setLoading(false);
  };

  const onDeleteClick = () => {
    showDialog({
      title: "Delete the selected users?",
      body: "If you proceed with this action, the selected users will be deleted and they can't login or use the platform anymore.",
      actions: [
        {
          label: "Cancel",
          color: "neutral",
          variant: "plain",
          onClick: (e, closeModal) => {
            closeModal();
          },
        },
        {
          label: "Delete",
          color: "danger",
          variant: "solid",
          onClick: (e, closeModal) => {
            selectedRows.length > 1 ? onDeleteUsers() : onDeleteUser();
            closeModal();
          },
        },
      ],
    });
  };

  useEffect(() => {
    if (prevFilter !== filter && prevFilter !== undefined) {
      if (filterTimeout.current) {
        clearTimeout(filterTimeout.current);
      }
      filterTimeout.current = setTimeout(() => {
        fetchUsers();
      }, 500);

      return () => {
        clearTimeout(filterTimeout.current);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, prevFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <>
      <Box position={"absolute"} top={0} left={0} width={"100vw"}>
        {loading ? <LinearProgress /> : null}
      </Box>
      <Typography level="h4" fontWeight={600} pt={2} px={2}>
        {t("Users")}
      </Typography>
      <Box
        display={"flex"}
        flexDirection={"row"}
        width={"100%"}
        py={2}
        px={1}
        boxSizing={"border-box"}
        justifyContent={selectedRows.length > 0 ? "flex-end" : "flex-start"}
        alignItems={"center"}
      >
        {selectedRows.length > 0 &&
        (isAllowed("users.write") || isAllowed("users.delete")) ? (
          <Box
            display={"flex"}
            flexDirection={"row"}
            flex={"1 1"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Box
              display={"flex"}
              flexDirection={"row"}
              flex={"1 1"}
              alignItems={"center"}
              gap={2}
            >
              <IconButton
                variant="plain"
                color="neutral"
                onClick={() => setSelectedRows([])}
              >
                <ArrowBack />
              </IconButton>
              <Typography level="h6">{selectedRows.length}</Typography>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              flex={"1 1"}
              alignItems={"center"}
              justifyContent={"flex-end"}
            >
              {selectedRows.length === 1 && isAllowed("users.write") ? (
                <IconButton
                  variant="plain"
                  color="neutral"
                  onClick={() => navigate(selectedRows[0])}
                >
                  <Edit />
                </IconButton>
              ) : null}
              {selectedRows.length === 1 && isAllowed("users.write") ? (
                <IconButton
                  variant="plain"
                  color="neutral"
                  onClick={() => setChangePwdOpen(true)}
                >
                  <Key />
                </IconButton>
              ) : null}
              {isAllowed("users.delete") ? (
                <IconButton
                  variant="plain"
                  color="danger"
                  onClick={onDeleteClick}
                >
                  <Delete />
                </IconButton>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Input
            size="sm"
            startDecorator={<Search />}
            fullWidth
            value={filter}
            placeholder={`${t("Type to search")}...`}
            onChange={(e) => setFilter(e.currentTarget.value)}
          />
        )}
      </Box>
      {isAllowed("users.write") ? (
        <IconButton
          sx={{
            position: "absolute",
            right: "1rem",
            bottom: "1rem",
            zIndex: 9,
            // boxShadow:
            //   "rgba(0, 0, 0, 0.05) 0px 3px 5px -1px, rgba(0, 0, 0, 0.10) -1px 1px 10px 0px, rgba(0, 0, 0, 0.10) 0px 1px 18px 0px",
          }}
          size="lg"
          onClick={() => navigate("new")}
        >
          <Add fontSize="large" />
        </IconButton>
      ) : null}
      <Box flex={"1 1 auto"} boxSizing={"border-box"} py={1}>
        <AutoSizer>
          {({ height, width }: Size) => (
            <InfiniteLoader
              threshold={5}
              itemCount={itemCount}
              isItemLoaded={isItemLoaded}
              loadMoreItems={loadMoreItems}
            >
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  itemCount={itemCount}
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  height={height}
                  width={width}
                  itemSize={60}
                  itemData={{
                    users,
                    selectedRows,
                    isItemLoaded,
                    setSelectedRows,
                    setUserDetailsModal,
                  }}
                  innerElementType={
                    users.length === 0
                      ? () => <CustomNoRowsOverlay label="No users found" />
                      : "div"
                  }
                >
                  {ListItem}
                </FixedSizeList>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </Box>
      <UserDetailsModal
        userId={userDetailsModal.userId}
        open={userDetailsModal.open}
        layout="fullscreen"
        onClose={() => setUserDetailsModal({ open: false, userId: "" })}
      />
      <ChangeUserPasswordModal
        open={changePwdOpen}
        layout="fullscreen"
        onClose={() => {
          setChangePwdOpen(false);
          setSelectedRows([]);
        }}
        userId={selectedRows[0]}
      />
    </>
  );
};

export default UsersMobileList;
